/**
 * TranslationFloating: 비디오 영역 하단 실시간 자막/번역 플로팅.
 *
 * 마크업: translate_2.html `.userMov_translationFloating` (일반 모드)
 *
 * 일반 모드: chatItem 스타일
 *   - 프로필 원형(이니셜+배경색) + 자막텍스트
 *   - partial 메시지(진행 중) 또는 번역 중: rtcNew_chatProgv2 로딩 인디케이터
 *   - 번역 완료: 번역문 표시 (내 발화는 자막 ON 상태에서 원문 표시)
 *
 * 동작 규칙:
 *   - 자막 ON: 확정(final) entry 외에도 진행 중 partial(`caption.currentTexts`)도 표시
 *   - 번역 ON: final 번역뿐 아니라 partial 번역도 표시
 *   - 동일 id가 재차 partial/final 로 갱신되면 5초 fade 타이머를 매번 초기화
 *   - 같은 화자에 대해 final 이 도착하면 partial 표시는 즉시 해당 final 로 교체
 *   - 5초 후 (마지막 갱신 기준) 페이드아웃
 */
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import type { CaptionEntry } from '@/store/slices/captionSlice';
import { getInitial, getProfileColor, toDisplayProfileUrl } from '@/utils/profileUtils';
import TypewriterText from '@/components/TypewriterText/TypewriterText';

const FADE_TIMEOUT = 5000;
const MAX_VISIBLE = 3;
/** 진행 중(partial) 합성 엔트리의 id prefix — 화자별로 유일 */
const PARTIAL_ID_PREFIX = '__partial__:';

/** 로딩 인디케이터 (v2 - 플로팅용) */
const TypingIndicatorV2: React.FC = () => (
  <span className="rtcNew_chatProgv2">
    <span className="rtcNew_chatProgv2Item" />
    <span className="rtcNew_chatProgv2Item" />
    <span className="rtcNew_chatProgv2Item" />
  </span>
);

const TranslationFloating: React.FC = () => {
  const isCaptionEnabled = useAppSelector((s) => s.translation.isCaptionEnabled);
  const isTranslateEnabled = useAppSelector((s) => s.translation.isTranslateEnabled);
  const fontSize = useAppSelector((s) => s.translation.fontSize);
  const myLang = useAppSelector((s) => s.translation.language);
  const entries = useAppSelector((s) => s.caption.entries);
  const currentTexts = useAppSelector((s) => s.caption.currentTexts);
  const participants = useAppSelector((s) => s.participants.participants);
  const local = useAppSelector((s) => s.local);

  // 현재 보이는 엔트리 ID 목록 (순서 유지)
  const visibleIdsRef = useRef<string[]>([]);
  // 이미 표시되었거나 표시 기회를 소진한 final entry id (재표시 방지)
  const shownIdsRef = useRef<Set<string>>(new Set());
  // 각 엔트리별 개별 fade 타이머
  const fadeTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // 각 엔트리의 마지막으로 본 표시 컨텐츠 스냅샷 (변경 감지 → 타이머 리셋)
  const snapshotsRef = useRef<Map<string, string>>(new Map());
  // 리렌더 트리거
  const [, setTick] = React.useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);

  const isLocalEntry = useCallback((entry: CaptionEntry): boolean => {
    return entry.userId === local.jitsiId || entry.userId === local.userId;
  }, [local.jitsiId, local.userId]);

  const getResolvedTranslation = useCallback((entry: CaptionEntry): string | null => {
    if (!isTranslateEnabled) return null;

    if (entry.multiLangData) {
      const originLang = entry.multiLangData.origin || 'ko';
      if (myLang !== originLang) {
        const translated = entry.multiLangData[myLang]?.trim();
        if (translated) return translated;
      }
    }

    const translated = entry.translatedText?.trim();
    return translated || null;
  }, [isTranslateEnabled, myLang]);

  /** 현재 myLang/번역 모드 기준 표시 가능 여부 */
  const isDisplayable = useCallback((entry: CaptionEntry): boolean => {
    if (entry.isSystem) return false;
    if (!entry.text?.trim()) return false;
    if (!isCaptionEnabled && !isTranslateEnabled) return false;

    // 자막+번역 ON 상태의 원격 발화는 번역문이 있을 때만 표시한다.
    if (isCaptionEnabled && (!isTranslateEnabled || isLocalEntry(entry))) return true;

    return !!getResolvedTranslation(entry);
  }, [getResolvedTranslation, isCaptionEnabled, isLocalEntry, isTranslateEnabled]);

  /** 표시 컨텐츠 스냅샷 (text + 번역문 + isFinal). 변경 감지용 */
  const computeSnapshot = useCallback((entry: CaptionEntry): string => {
    let translation = '';
    if (entry.multiLangData) {
      const originLang = entry.multiLangData.origin || 'ko';
      if (myLang !== originLang) translation = entry.multiLangData[myLang] || '';
    }
    if (!translation && entry.translatedText) {
      translation = entry.translatedText;
    }
    return `${entry.text}|${translation}|${entry.isFinal ? 1 : 0}`;
  }, [myLang]);

  /** 엔트리별 fade 타이머 (재)시작. 기존 타이머가 있으면 리셋 */
  const restartFadeTimer = useCallback((entryId: string) => {
    const existing = fadeTimers.current.get(entryId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      fadeTimers.current.delete(entryId);
      snapshotsRef.current.delete(entryId);
      visibleIdsRef.current = visibleIdsRef.current.filter((id) => id !== entryId);
      rerender();
    }, FADE_TIMEOUT);
    fadeTimers.current.set(entryId, timer);
  }, [rerender]);

  /** visible에서 제거 + 타이머/스냅샷 정리 */
  const removeVisible = useCallback((entryId: string) => {
    const t = fadeTimers.current.get(entryId);
    if (t) {
      clearTimeout(t);
      fadeTimers.current.delete(entryId);
    }
    snapshotsRef.current.delete(entryId);
    visibleIdsRef.current = visibleIdsRef.current.filter((x) => x !== entryId);
  }, []);

  /** partial 합성 엔트리 (현재 진행 중인 발화) */
  const partialEntries = useMemo<CaptionEntry[]>(() => {
    return Object.values(currentTexts)
      .sort((a, b) => a.updatedAt - b.updatedAt)
      .map((partial) => {
        const isLocal = partial.speakerId === local.jitsiId || partial.speakerId === local.userId;
        const p = !isLocal ? participants[partial.speakerId] : undefined;
        const userName = isLocal
          ? (local.userName || local.nickname || local.userId || '')
          : (p?.displayName || p?.nickname || partial.speakerName || partial.speakerId);
        const nickname = isLocal ? (local.nickname || '') : (p?.nickname || partial.nickname || '');
        return {
          id: `${PARTIAL_ID_PREFIX}${partial.speakerId}`,
          userId: partial.speakerId,
          userName,
          nickname,
          text: partial.text,
          timestamp: partial.updatedAt,
          isFinal: false,
          translatedText: partial.translatedText || null,
          multiLangData: partial.multiLangData ?? null,
        };
      });
  }, [
    currentTexts,
    participants,
    local.jitsiId,
    local.userId,
    local.userName,
    local.nickname,
  ]);

  // 엔트리/partial 변동 감지 → visible 갱신 + 스냅샷 변경 시 타이머 리셋
  useEffect(() => {
    let changed = false;

    // 통합 후보 맵 (entries + partial)
    const itemMap = new Map<string, CaptionEntry>();
    for (const e of entries) itemMap.set(e.id, e);
    for (const partialEntry of partialEntries) itemMap.set(partialEntry.id, partialEntry);

    // 1) 사라진 partial 합성 엔트리 즉시 제거
    //    (다른 화자가 발화 시작 / clearCurrentText / final 로 교체된 경우)
    for (const id of [...visibleIdsRef.current]) {
      if (id.startsWith(PARTIAL_ID_PREFIX) && !itemMap.has(id)) {
        removeVisible(id);
        changed = true;
      }
    }

    // 2) 여전히 보이는 엔트리: 컨텐츠 변경 감지 → 타이머 리셋
    for (const id of [...visibleIdsRef.current]) {
      const entry = itemMap.get(id);
      if (!entry || !isDisplayable(entry)) {
        removeVisible(id);
        changed = true;
        continue;
      }
      const snap = computeSnapshot(entry);
      const last = snapshotsRef.current.get(id);
      if (last !== snap) {
        snapshotsRef.current.set(id, snap);
        restartFadeTimer(id);
        changed = true;
      }
    }

    // 3) 새 final 엔트리 추가 (마지막 entry 만 평가)
    const lastFinal = entries[entries.length - 1];
    if (
      lastFinal &&
      !lastFinal.isSystem &&
      !shownIdsRef.current.has(lastFinal.id) &&
      !visibleIdsRef.current.includes(lastFinal.id) &&
      isDisplayable(lastFinal)
    ) {
      // 같은 화자의 partial 이 표시 중이라면 즉시 제거 (final 로 자연스러운 교체)
      const partialIdForSpeaker = `${PARTIAL_ID_PREFIX}${lastFinal.userId}`;
      if (visibleIdsRef.current.includes(partialIdForSpeaker)) {
        removeVisible(partialIdForSpeaker);
      }

      shownIdsRef.current.add(lastFinal.id);
      visibleIdsRef.current = [...visibleIdsRef.current, lastFinal.id];
      while (visibleIdsRef.current.length > MAX_VISIBLE) {
        const removedId = visibleIdsRef.current.shift()!;
        removeVisible(removedId);
      }
      snapshotsRef.current.set(lastFinal.id, computeSnapshot(lastFinal));
      restartFadeTimer(lastFinal.id);
      changed = true;
    }

    // 4) partial 엔트리 추가 (아직 보이지 않는 경우)
    for (const partialEntry of partialEntries) {
      if (
        !visibleIdsRef.current.includes(partialEntry.id) &&
        isDisplayable(partialEntry)
      ) {
        visibleIdsRef.current = [...visibleIdsRef.current, partialEntry.id];
        while (visibleIdsRef.current.length > MAX_VISIBLE) {
          const removedId = visibleIdsRef.current.shift()!;
          removeVisible(removedId);
        }
        snapshotsRef.current.set(partialEntry.id, computeSnapshot(partialEntry));
        restartFadeTimer(partialEntry.id);
        changed = true;
      }
    }

    // 5) 번역 모드: 늦게 도착한 번역으로 표시 가능해진 과거 entry 1개 추가
    if (isTranslateEnabled) {
      for (let i = entries.length - 1; i >= Math.max(0, entries.length - MAX_VISIBLE * 2); i--) {
        const entry = entries[i];
        if (entry.isSystem) continue;
        if (shownIdsRef.current.has(entry.id)) continue;
        if (visibleIdsRef.current.includes(entry.id)) continue;
        if (!isDisplayable(entry)) continue;

        const partialIdForSpeaker = `${PARTIAL_ID_PREFIX}${entry.userId}`;
        if (visibleIdsRef.current.includes(partialIdForSpeaker)) {
          removeVisible(partialIdForSpeaker);
        }

        shownIdsRef.current.add(entry.id);
        visibleIdsRef.current = [...visibleIdsRef.current, entry.id];
        while (visibleIdsRef.current.length > MAX_VISIBLE) {
          const removedId = visibleIdsRef.current.shift()!;
          removeVisible(removedId);
        }
        snapshotsRef.current.set(entry.id, computeSnapshot(entry));
        restartFadeTimer(entry.id);
        changed = true;
        break;
      }
    }

    // shownIdsRef 정리 (200개 초과 시)
    if (shownIdsRef.current.size > 200) {
      const currentIds = new Set(entries.map((e) => e.id));
      for (const id of shownIdsRef.current) {
        if (!currentIds.has(id)) shownIdsRef.current.delete(id);
      }
    }

    if (changed) rerender();
  }, [
    entries,
    partialEntries,
    isTranslateEnabled,
    isDisplayable,
    computeSnapshot,
    restartFadeTimer,
    removeVisible,
    rerender,
  ]);

  // 클린업
  useEffect(() => {
    return () => {
      fadeTimers.current.forEach((timer) => clearTimeout(timer));
      fadeTimers.current.clear();
      snapshotsRef.current.clear();
    };
  }, []);

  // 자막/번역 둘 다 꺼져있으면 렌더링하지 않음
  if (!isCaptionEnabled && !isTranslateEnabled) return null;

  // visible 엔트리 추출 (entries + partial)
  const itemMap = new Map<string, CaptionEntry>();
  for (const e of entries) itemMap.set(e.id, e);
  for (const partialEntry of partialEntries) itemMap.set(partialEntry.id, partialEntry);

  const displayed = visibleIdsRef.current
    .map((id) => itemMap.get(id))
    .filter((e): e is CaptionEntry => !!e && isDisplayable(e));

  if (displayed.length === 0) return null;

  const fontScale = fontSize / 100;

  return (
    <div className="userMov_translationFloating">
      {displayed.map((entry) => {
        const isLocal = entry.userId === local.jitsiId || entry.userId === local.userId;
        const participant = !isLocal ? participants[entry.userId] : undefined;
        const avatarUrl = isLocal
          ? toDisplayProfileUrl(local.profileUrl, local.avatarUrl)
          : toDisplayProfileUrl(participant?.profileUrl || entry.profileUrl, participant?.avatarUrl);
        const displayName = entry.nickname || entry.userName;
        const resolvedTranslation = getResolvedTranslation(entry);
        const showOriginal = isCaptionEnabled && (!isTranslateEnabled || isLocal);
        const showTranslation = !showOriginal && !!resolvedTranslation;

        if (!showOriginal && !showTranslation) return null;

        // 내 발화는 자막 ON 상태에서 원문 표시, 그 외 번역 모드는 번역문 표시
        const displayText = showTranslation ? resolvedTranslation : entry.text;
        // partial(=진행 중) 또는 번역 진행 중일 때 인디케이터 표시
        const isInProgress = !entry.isFinal || !!entry.isTranslating;

        return (
          <div
            key={entry.id}
            className={`userMov_translationFloating__chatItem${
              showTranslation ? ' type_trans' : ''
            }`}
          >
            {avatarUrl ? (
              <div className="profileBox">
                <div
                  className="profileBox__img"
                  style={{ backgroundImage: `url(${avatarUrl})` }}
                />
              </div>
            ) : (
              <div
                className="profileBox"
                style={{ background: getProfileColor(displayName) }}
              >
                <span className="profileBox__name">{getInitial(displayName)}</span>
              </div>
            )}
            <p
              className="userMov_translationFloating__text"
              style={{ fontSize: `${fontScale}em` }}
            >
              {/* 원문은 STT 수정 시 prefix 유지 후 재타이핑, 번역문은 재번역 시 즉시 교체(append).
                  레인 기록은 진행 중(partial) 아이템만 — final은 seed로 읽기만 한다 */}
              <TypewriterText
                text={displayText ?? ''}
                animKey={showTranslation ? `float-tr:${entry.userId}` : `float:${entry.userId}`}
                mode={showTranslation ? 'append' : 'retype'}
                laneWrite={!entry.isFinal}
              />
              {isInProgress && <TypingIndicatorV2 />}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default TranslationFloating;
